import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { academicService } from '../services/academicService'
import { transcriptService } from '../services/transcriptService'
import { calculateLetterGrade } from '../utils/gradeUtils'

const statusStyles = {
  completed: 'bg-green-100 text-green-800',
  in_progress: 'bg-amber-100 text-amber-800',
  not_taken: 'bg-neutral-100 text-neutral-600'
}

const statusLabel = {
  completed: 'Completed',
  in_progress: 'In progress',
  not_taken: 'Not yet taken'
}

export default function TranscriptPage() {
  const { studentId: studentIdParam } = useParams()
  const navigate = useNavigate()
  const [viewer, setViewer] = useState(null)
  const [student, setStudent] = useState(null)
  const [students, setStudents] = useState([])
  const [rows, setRows] = useState([])
  const [subjects, setSubjects] = useState([])
  const [terms, setTerms] = useState([])
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [search, setSearch] = useState('')
  const [isReady, setIsReady] = useState(true)
  const [backfill, setBackfill] = useState({
    subjectId: '',
    termId: '',
    teacherName: '',
    percent: '',
    notes: ''
  })
  const [isSaving, setIsSaving] = useState(false)

  const isAdmin = viewer?.role === 'admin'
  const isTeacher = viewer?.role === 'instructor'
  const canManage = isAdmin || isTeacher
  const targetStudentId = studentIdParam
    ? Number(studentIdParam)
    : viewer?.role === 'student'
      ? viewer.id
      : null

  useEffect(() => {
    const authData = sessionStorage.getItem('user_auth')
    if (!authData) {
      navigate('/login')
      return
    }
    const { user } = JSON.parse(authData)
    setViewer(user)
  }, [navigate])

  useEffect(() => {
    if (!viewer) return

    if (studentIdParam && viewer.role === 'student' && String(viewer.id) !== String(studentIdParam)) {
      setError('You can only open your own transcript.')
      setLoading(false)
      return
    }

    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const ready = await academicService.isReady()
        setIsReady(ready)
        if (!ready) {
          setLoading(false)
          return
        }

        if (isAdmin) {
          const { data, error: studentsError } = await supabase
            .from('users')
            .select('id, name, code, role')
            .eq('role', 'student')
            .order('name', { ascending: true })
          if (studentsError) throw studentsError
          setStudents(data || [])
        } else if (isTeacher) {
          const teacherStudents = await academicService.listStudentsForTeacher(viewer.id)
          setStudents(teacherStudents)
        }

        if (!targetStudentId) {
          setLoading(false)
          return
        }

        const { data: studentRow, error: studentError } = await supabase
          .from('users')
          .select('id, name, code, role')
          .eq('id', targetStudentId)
          .single()
        if (studentError) throw studentError
        setStudent(studentRow)

        const [progress, subjectRows, termRows, teacherRows] = await Promise.all([
          transcriptService.getCatalogWithProgress(targetStudentId),
          academicService.listSubjects(),
          academicService.listTerms(),
          academicService.listTeachers()
        ])
        setRows(progress)
        setSubjects(subjectRows)
        setTerms(termRows)
        setTeachers(teacherRows)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [viewer, studentIdParam, targetStudentId, isAdmin, isTeacher, navigate])

  const filteredStudents = useMemo(() => {
    if (!search.trim()) return students
    const needle = search.toLowerCase()
    return students.filter((item) => (
      item.name?.toLowerCase().includes(needle) ||
      item.code?.toLowerCase().includes(needle)
    ))
  }, [students, search])

  const counts = rows.reduce((acc, row) => {
    acc[row.status] += 1
    return acc
  }, { completed: 0, in_progress: 0, not_taken: 0 })

  const handleToggleComplete = async (row) => {
    if (!targetStudentId || !canManage) return
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      if (row.entry) {
        await transcriptService.deleteEntry(row.entry.id)
      } else {
        const currentTerm = terms.find((term) => term.is_current)
        await transcriptService.upsertEntry({
          studentId: targetStudentId,
          subjectId: row.subject.id,
          termId: currentTerm?.id || null,
          teacherName: viewer?.name || null,
          source: 'backfill'
        })
      }
      const progress = await transcriptService.getCatalogWithProgress(targetStudentId)
      setRows(progress)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleBackfill = async (event) => {
    event.preventDefault()
    if (!targetStudentId) return
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const percentValue = backfill.percent === '' ? null : Number(backfill.percent)
      await transcriptService.upsertEntry({
        studentId: targetStudentId,
        subjectId: Number(backfill.subjectId),
        termId: backfill.termId ? Number(backfill.termId) : null,
        teacherName: backfill.teacherName || null,
        percent: percentValue,
        letterGrade: calculateLetterGrade(percentValue),
        source: 'backfill',
        notes: backfill.notes
      })
      setSuccess('Transcript updated.')
      setBackfill({ subjectId: '', termId: '', teacherName: '', percent: '', notes: '' })
      const progress = await transcriptService.getCatalogWithProgress(targetStudentId)
      setRows(progress)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 loading-spinner"></div>
      </div>
    )
  }

  if (!isReady) {
    return (
      <div className="container-mobile py-10">
        <h1 className="text-2xl font-bold text-neutral-900 mb-2">Transcript</h1>
        <p className="text-neutral-600">
          Transcript tables are not installed yet. Ask an admin to run
          {' '}<code className="text-xs bg-neutral-100 px-1 py-0.5 rounded">sql/20260912_classes_and_transcripts.sql</code>.
        </p>
      </div>
    )
  }

  return (
    <div className="container-mobile py-6 pb-16">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-neutral-600 mb-4"
        aria-label="Go back"
      >
        ← Back
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Transcript</h1>
          <p className="text-neutral-600">
            {student ? `${student.name}${student.code ? ` · ${student.code}` : ''}` : 'Choose a student'}
          </p>
        </div>
        {student && (
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">{counts.completed} completed</span>
            <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800">{counts.in_progress} in progress</span>
            <span className="px-2 py-1 rounded-full bg-neutral-100 text-neutral-600">{counts.not_taken} remaining</span>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm mb-4" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm mb-4">
          {success}
        </div>
      )}

      {canManage && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">Find student</label>
          <input
            className="input w-full mb-3"
            placeholder="Search by name or code"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="max-h-40 overflow-y-auto divide-y divide-neutral-100">
            {filteredStudents.slice(0, 20).map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(`/dashboard/transcript/${item.id}`)}
                className={`w-full text-left py-2 text-sm ${
                  String(item.id) === String(targetStudentId) ? 'text-primary-700 font-semibold' : 'text-neutral-800'
                }`}
              >
                {item.name} {item.code ? `(${item.code})` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {student && (
        <div className="space-y-3 mb-8">
          {rows.map((row) => (
            <div key={row.subject.id} className="bg-white rounded-2xl border border-neutral-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-neutral-900">{row.subject.name}</p>
                  {row.entry?.term?.name && (
                    <p className="text-sm text-neutral-500 mt-1">
                      {row.entry.term.name}
                      {row.entry.teacher_name ? ` · ${row.entry.teacher_name}` : ''}
                      {row.entry.letter_grade ? ` · ${row.entry.letter_grade}` : ''}
                      {row.entry.percent !== null && row.entry.percent !== undefined ? ` (${row.entry.percent}%)` : ''}
                    </p>
                  )}
                  {row.status === 'in_progress' && row.currentClass && (
                    <p className="text-sm text-amber-700 mt-1">
                      Currently in {row.currentClass.name}
                    </p>
                  )}
                  {row.entry?.source === 'backfill' && (
                    <p className="text-xs text-neutral-400 mt-1">Backfilled</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canManage ? (
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        checked={row.status === 'completed'}
                        disabled={isSaving}
                        onChange={() => handleToggleComplete(row)}
                        aria-label={`Mark ${row.subject.name} complete`}
                      />
                      Complete
                    </label>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded-full ${statusStyles[row.status]}`}>
                      {statusLabel[row.status]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && student && (
        <form onSubmit={handleBackfill} className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">Add to transcript</h2>
            <p className="text-sm text-neutral-500">
              Check a subject off above, or add details here. This does not create a live class.
            </p>
          </div>
          <label className="block text-sm font-medium text-neutral-700">
            Subject
            <select
              required
              className="input w-full mt-1"
              value={backfill.subjectId}
              onChange={(event) => setBackfill((prev) => ({ ...prev, subjectId: event.target.value }))}
            >
              <option value="">Select subject</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>{subject.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Term
            <select
              className="input w-full mt-1"
              value={backfill.termId}
              onChange={(event) => setBackfill((prev) => ({ ...prev, termId: event.target.value }))}
            >
              <option value="">Unknown / other</option>
              {terms.map((term) => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Teacher
            <input
              className="input w-full mt-1"
              list="transcript-teachers"
              value={backfill.teacherName}
              onChange={(event) => setBackfill((prev) => ({ ...prev, teacherName: event.target.value }))}
              placeholder="Teacher name"
            />
            <datalist id="transcript-teachers">
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.name} />
              ))}
            </datalist>
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Grade %
            <input
              type="number"
              min="0"
              max="100"
              step="any"
              className="input w-full mt-1"
              value={backfill.percent}
              onChange={(event) => setBackfill((prev) => ({ ...prev, percent: event.target.value }))}
              placeholder="Optional"
            />
          </label>
          <label className="block text-sm font-medium text-neutral-700">
            Notes
            <input
              className="input w-full mt-1"
              value={backfill.notes}
              onChange={(event) => setBackfill((prev) => ({ ...prev, notes: event.target.value }))}
              placeholder="Optional"
            />
          </label>
          <button type="submit" className="btn-primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save on transcript'}
          </button>
        </form>
      )}
    </div>
  )
}
